// client/src/components/clientes/uploads/GaleriaAntesDepois.js
import React, { useState, useRef, useEffect } from 'react';
import { useArquivos, useMutateArquivos } from '../../../hooks/useArquivos';
import { UploadCloud, Loader2, Trash2, ImageOff, ZoomIn, AlertCircle, RefreshCw } from 'lucide-react';
import ImageZoomModal from '../../common/ImageZoomModal';

const GaleriaSecao = ({ titulo, categoria, clienteId, arquivos, onUpload, onDelete, loading }) => {
    const [fileToUpload, setFileToUpload] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isZoomOpen, setIsZoomOpen] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const imageFile = e.target.files[0];
        if (!imageFile) return;
        
        // Resetar estados
        setUploadError(null);
        setUploadProgress(0);
        
        // Validações
        if (!imageFile.type.startsWith('image/')) {
            setUploadError('Por favor, selecione apenas arquivos de imagem (JPG, PNG, GIF, WebP).');
            return;
        }

        if (imageFile.size > 10 * 1024 * 1024) {
            setUploadError('A imagem deve ter no máximo 10MB.');
            return;
        }

        setFileToUpload(imageFile);
    };

    const handleUpload = () => {
        if (!fileToUpload) return;
        
        // Resetar erro
        setUploadError(null);
        setUploadProgress(0);
        
        // ✅ CORREÇÃO CRÍTICA: Usar campo 'imagem' para o upload de imagens
        const formData = new FormData();
        formData.append('imagem', fileToUpload); // ✅ MUDAR DE 'file' PARA 'imagem'
        formData.append('referenciaId', clienteId);
        formData.append('model', 'CLIENTE'); // ✅ SEMPRE UPPERCASE
        formData.append('categoria', categoria.toUpperCase()); // ✅ SEMPRE UPPERCASE
        
        console.log('📤 [Galeria] Preparando upload:', {
            nomeArquivo: fileToUpload.name,
            tamanho: fileToUpload.size,
            clienteId,
            categoria: categoria.toUpperCase()
        });
        
        // Simular progresso
        const progressInterval = setInterval(() => {
            setUploadProgress(prev => Math.min(prev + 10, 90));
        }, 100);
        
        // Fazer upload
        onUpload(formData, {
            onSuccess: () => {
                clearInterval(progressInterval);
                setUploadProgress(100);
                
                // Sucesso - limpar estados
                setTimeout(() => {
                    setFileToUpload(null);
                    setUploadProgress(0);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                }, 500);
            },
            onError: (error) => {
                clearInterval(progressInterval);
                
                console.error('❌ [Galeria] Erro no upload:', error);
                
                // Tratar erros específicos
                let errorMessage = 'Erro ao fazer upload da imagem.';
                
                if (error.response?.status === 400) {
                    const errorData = error.response?.data;
                    
                    if (errorData?.message?.includes('Tipo de arquivo não suportado')) {
                        errorMessage = 'Tipo de arquivo não suportado. Use apenas imagens (JPG, PNG, GIF, WebP).';
                    } else if (errorData?.message?.includes('tamanho máximo')) {
                        errorMessage = 'Arquivo muito grande. Tamanho máximo: 10MB.';
                    } else if (errorData?.message?.includes('referenciaId')) {
                        errorMessage = 'ID do cliente inválido.';
                    } else if (errorData?.message?.includes('model')) {
                        errorMessage = 'Modelo inválido.';
                    } else if (errorData?.message) {
                        errorMessage = errorData.message;
                    }
                } else if (error.code === 'ECONNABORTED') {
                    errorMessage = 'Tempo limite excedido. Tente um arquivo menor.';
                } else if (error.code === 'NETWORK_ERROR') {
                    errorMessage = 'Erro de rede. Verifique sua conexão.';
                }
                
                setUploadError(errorMessage);
                
                // Manter o arquivo para tentar novamente
                setTimeout(() => setUploadError(null), 5000);
            }
        });
    };

    const handleImageClick = (arquivo) => {
        setSelectedImage(arquivo);
        setIsZoomOpen(true);
    };

    const handleCancelUpload = () => {
        setFileToUpload(null);
        setUploadError(null);
        setUploadProgress(0);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    // Tipos de imagem permitidos
    const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];

    return (
        <div className="gallery-section">
            <h4 className="gallery-section-title">{titulo}</h4>
            
            {/* Área de Upload */}
            <div className="gallery-upload-area">
                <input 
                    type="file" 
                    ref={fileInputRef} 
                    onChange={handleFileChange} 
                    className="form-input" 
                    accept={ALLOWED_IMAGE_TYPES.join(',')}
                    disabled={loading}
                />
                
                {/* Informações do arquivo selecionado */}
                {fileToUpload && (
                    <div className="upload-file-info">
                        <div className="file-details">
                            <span className="file-name">{fileToUpload.name}</span>
                            <span className="file-size">({(fileToUpload.size / 1024 / 1024).toFixed(2)} MB)</span>
                        </div>
                        
                        {/* Barra de progresso */}
                        {uploadProgress > 0 && uploadProgress < 100 && (
                            <div className="upload-progress">
                                <div 
                                    className="progress-bar" 
                                    style={{ width: `${uploadProgress}%` }}
                                />
                                <span className="progress-text">{uploadProgress}%</span>
                            </div>
                        )}
                        
                        {/* Botões de ação — .btn-upload/.btn-cancel não tinham
                            CSS em lugar nenhum, renderizavam sem estilo.
                            Trocados pelas classes action-button padrão. */}
                        <div className="upload-actions actions-bar">
                            <button
                                type="button"
                                onClick={handleUpload}
                                disabled={loading || uploadProgress > 0}
                                className="action-button action-button-primary"
                            >
                                <UploadCloud size={18} className="icon" />
                                {uploadProgress > 0 ? 'Enviando...' : 'Enviar'}
                            </button>

                            <button
                                type="button"
                                onClick={handleCancelUpload}
                                className="action-button action-button-secondary"
                                disabled={loading && uploadProgress > 0}
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                )}
                
                {/* Mensagem de erro */}
                {uploadError && (
                    <div className="upload-error">
                        <AlertCircle size={16} />
                        <span>{uploadError}</span>
                    </div>
                )}
                
                {/* Dicas de upload */}
                {!fileToUpload && (
                    <div className="upload-hints">
                        <p className="hint-text">
                            Clique no botão acima para selecionar uma imagem.
                        </p>
                        <p className="hint-text">
                            Tipos permitidos: JPG, PNG, GIF, WebP, SVG (máx. 10MB)
                        </p>
                    </div>
                )}
            </div>
            
            {/* Grid de Imagens */}
            <div className="gallery-grid-compact">
                {arquivos.length === 0 ? (
                    <div className="gallery-empty-state">
                        <ImageOff size={32} className="empty-icon" />
                        <p className="empty-text">
                            Nenhuma imagem enviada.
                        </p>
                        <p className="empty-hint">
                            Faça upload da primeira imagem usando o campo acima.
                        </p>
                    </div>
                ) : (
                    arquivos.map(arquivo => {
                        // ✅ CORREÇÃO: Garantir que a URL seja acessível
                        const imageUrl = arquivo.url || arquivo.arquivo;
                        
                        return (
                            <div key={arquivo._id} className="gallery-item">
                                <div className="gallery-image-container">
                                    <img 
                                        src={imageUrl} 
                                        alt={`${titulo} - ${new Date(arquivo.createdAt).toLocaleDateString('pt-BR')}`}
                                        className="gallery-image"
                                        onClick={() => handleImageClick(arquivo)}
                                        onError={(e) => {
                                            console.warn(`❌ Erro ao carregar imagem: ${imageUrl}`);
                                            e.target.src = '/images/image-error.png';
                                            e.target.alt = 'Imagem não carregada';
                                        }}
                                    />
                                    
                                    {/* Botão de zoom */}
                                    <button 
                                        className="gallery-zoom-btn"
                                        onClick={() => handleImageClick(arquivo)}
                                        title="Ampliar imagem"
                                    >
                                        <ZoomIn size={16} />
                                    </button>
                                    
                                    {/* Overlay de informações */}
                                    <div className="image-overlay">
                                        <span className="image-date">
                                            {new Date(arquivo.createdAt).toLocaleDateString('pt-BR')}
                                        </span>
                                        
                                        {/* Tamanho do arquivo */}
                                        {arquivo.metadata?.size && (
                                            <span className="image-size">
                                                {(arquivo.metadata.size / 1024 / 1024).toFixed(1)} MB
                                            </span>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Informações do arquivo */}
                                <div className="gallery-item-info">
                                    <div className="item-main-info">
                                        <span className="item-date">
                                            {new Date(arquivo.createdAt).toLocaleDateString('pt-BR')}
                                        </span>
                                        
                                        {arquivo.metadata?.originalName && (
                                            <span className="item-name" title={arquivo.metadata.originalName}>
                                                {arquivo.metadata.originalName.length > 20 
                                                    ? arquivo.metadata.originalName.substring(0, 20) + '...'
                                                    : arquivo.metadata.originalName}
                                            </span>
                                        )}
                                    </div>
                                    
                                    {/* Botão de exclusão */}
                                    <button 
                                        type="button" 
                                        className="gallery-item-delete-btn" 
                                        title="Excluir Imagem" 
                                        onClick={() => {
                                            if (window.confirm('Tem certeza que deseja excluir esta imagem?')) {
                                                onDelete({ 
                                                    arquivoId: arquivo._id, 
                                                    params: { 
                                                        referenciaId: clienteId, 
                                                        model: 'CLIENTE' 
                                                    } 
                                                });
                                            }
                                        }}
                                        disabled={loading}
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Modal de Zoom */}
            {selectedImage && (
                <ImageZoomModal
                    image={selectedImage}
                    isOpen={isZoomOpen}
                    onClose={() => {
                        setIsZoomOpen(false);
                        setTimeout(() => setSelectedImage(null), 300);
                    }}
                />
            )}
        </div>
    );
};

const GaleriaAntesDepois = ({ clienteId }) => {
    console.log('🎨 [GaleriaAntesDepois] Iniciando com clienteId:', clienteId);
    
    // ✅ PARÂMETROS CORRETOS PARA A API
    const queryParams = { 
        referenciaId: clienteId, 
        model: 'CLIENTE' // ✅ SEMPRE UPPERCASE
    };
    
    const { 
        data: galeria = [], 
        isLoading: galeriaLoading,
        refetch,
        error: galeriaError 
    } = useArquivos(queryParams);
    
    const { uploadArquivo, removeArquivo, isPending: isMutating } = useMutateArquivos();

    // ✅ DEBUG: Log dos dados recebidos
    useEffect(() => {
        if (galeria.length > 0) {
            console.log('📊 [GaleriaAntesDepois] Dados recebidos:', {
                totalArquivos: galeria.length,
                primeiroArquivo: galeria[0],
                antes: galeria.filter(a => a.categoria === 'ANTES').length,
                depois: galeria.filter(a => a.categoria === 'DEPOIS').length,
                todasCategorias: [...new Set(galeria.map(a => a.categoria))]
            });
            
            // Verificar URLs
            galeria.forEach((arquivo, index) => {
                if (!arquivo.url && !arquivo.arquivo) {
                    console.warn(`⚠️  Arquivo ${index} sem URL:`, arquivo);
                }
            });
        }
    }, [galeria]);

    // ✅ Filtrar por categoria (usando uppercase para garantir)
    const arquivosAntes = galeria.filter(a => 
        (a.categoria || '').toUpperCase() === 'ANTES'
    );
    
    const arquivosDepois = galeria.filter(a => 
        (a.categoria || '').toUpperCase() === 'DEPOIS'
    );

    // ✅ Função para forçar refresh da galeria
    const handleRefresh = () => {
        console.log('🔄 [GaleriaAntesDepois] Forçando refresh...');
        refetch();
    };

    // ✅ Estado de loading
    if (galeriaLoading && galeria.length === 0) {
        return (
            <div className="galeria-loading-state">
                <div className="loading-content">
                    <Loader2 className="animate-spin loading-spinner" size={24} />
                    <p className="loading-text">Carregando galeria...</p>
                    <p className="loading-hint">Isso pode levar alguns instantes</p>
                </div>
            </div>
        );
    }

    // ✅ Estado de erro
    if (galeriaError) {
        return (
            <div className="galeria-error-state">
                <div className="error-content">
                    <AlertCircle size={32} className="error-icon" />
                    <h3 className="error-title">Erro ao carregar galeria</h3>
                    <p className="error-message">{galeriaError.message || 'Ocorreu um erro ao buscar as imagens.'}</p>
                    {/* .error-retry-btn não tinha CSS em lugar nenhum — botão
                        renderizava sem estilo nenhum. Trocado pelo padrão
                        action-button-primary usado no mesmo cenário (erro +
                        retry) em Clientes.js. */}
                    <button
                        onClick={handleRefresh}
                        className="action-button action-button-primary"
                    >
                        <RefreshCw size={18} className="icon" />
                        Tentar novamente
                    </button>
                </div>
            </div>
        );
    }

    // ✅ Debug panel (somente em desenvolvimento)
    const showDebug = process.env.NODE_ENV === 'development';
    
    return (
        <div className="galeria-antes-depois-container">
            {/* Header da galeria */}
            <div className="galeria-header">
                <h3 className="galeria-title">Galeria de Acompanhamento</h3>
                
                <div className="galeria-stats">
                    <span className="stat-item">
                        <strong>{galeria.length}</strong> {galeria.length === 1 ? 'imagem' : 'imagens'} no total
                    </span>
                    
                    <span className="stat-item">
                        <strong>{arquivosAntes.length}</strong> antes
                    </span>
                    
                    <span className="stat-item">
                        <strong>{arquivosDepois.length}</strong> depois
                    </span>
                    
                    {/* .refresh-btn não tinha CSS em lugar nenhum — trocado
                        pelo padrão action-icon-button (botão de ícone avulso,
                        sem estado de toggle — mesmo usado no kebab de
                        PedidoRow/VendaRow/UnidadeRow etc. em todo o app) */}
                    <button
                        onClick={handleRefresh}
                        disabled={galeriaLoading}
                        className="action-icon-button"
                        title="Atualizar galeria"
                    >
                        <Loader2 size={16} className={galeriaLoading ? 'animate-spin' : ''} />
                    </button>
                </div>
            </div>
            
            {/* Debug info (apenas desenvolvimento) */}
            {showDebug && (
                <div className="debug-panel">
                    <details>
                        <summary>Debug Info</summary>
                        <pre className="debug-content">
                            {JSON.stringify({
                                clienteId,
                                queryParams,
                                totalArquivos: galeria.length,
                                antesCount: arquivosAntes.length,
                                depoisCount: arquivosDepois.length,
                                primeiraImagem: galeria[0] ? {
                                    id: galeria[0]._id,
                                    url: galeria[0].url,
                                    categoria: galeria[0].categoria,
                                    hasUrl: !!galeria[0].url
                                } : null,
                                loading: galeriaLoading,
                                error: galeriaError?.message
                            }, null, 2)}
                        </pre>
                    </details>
                </div>
            )}
            
            {/* Grid principal */}
            <div className="galeria-grid">
                <GaleriaSecao
                    titulo="📸 Fotos de Antes"
                    categoria="ANTES" // ✅ SEMPRE UPPERCASE
                    clienteId={clienteId}
                    arquivos={arquivosAntes}
                    onUpload={uploadArquivo.mutate}
                    onDelete={removeArquivo.mutate}
                    loading={isMutating}
                />
                
                <div className="galeria-divider" />
                
                <GaleriaSecao
                    titulo="🌟 Fotos de Depois"
                    categoria="DEPOIS" // ✅ SEMPRE UPPERCASE
                    clienteId={clienteId}
                    arquivos={arquivosDepois}
                    onUpload={uploadArquivo.mutate}
                    onDelete={removeArquivo.mutate}
                    loading={isMutating}
                />
            </div>
            
            {/* Instruções de uso */}
            <div className="galeria-instructions">
                <h4>💡 Como usar a galeria:</h4>
                <ul>
                    <li><strong>Fotos de Antes:</strong> Registre o estado inicial do cliente antes dos tratamentos</li>
                    <li><strong>Fotos de Depois:</strong> Documente os resultados após os procedimentos</li>
                    <li><strong>Organização:</strong> As fotos são automaticamente organizadas por data de upload</li>
                    <li><strong>Tamanho máximo:</strong> 10MB por imagem</li>
                    <li><strong>Formatos suportados:</strong> JPG, PNG, GIF, WebP, SVG</li>
                </ul>
            </div>
        </div>
    );
};

export default GaleriaAntesDepois;